import { useState, useEffect, useReducer } from "react";
import { Box, Card, CardContent, Divider, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody, Stack, Tooltip, IconButton, InputAdornment } from "@mui/material";
import { z } from "zod";
import { formatCurrency } from "../../common/Helper";
import WarningIcon 		from '@mui/icons-material/Warning';

const DividendDiscountModel = ({ company, dividends, indicators, stockPrice, inflation }) => {

	const [input, dispatch] = useReducer(reducer, {
		lastDividend	: 0,
		growthRate		: 5,
		discountRate	: 9,
		projectionYears	: 10,
		gLongTerm		: 5,
		isValid			: false,
		errors			: {}
	});

	const [projected, setProjected] 		= useState([]);
	const [fairPrice, setFairPrice] 		= useState(null);
	const [pvTerminal, setPvTerminal] 		= useState(null);
	const [usageWarning, setUsageWarning]	= useState(false)

	useEffect(() => {
		if (indicators)	{
			setUsageWarning(indicators.find(i => i.id == 'dividendGrowthStreak')?.value >= 5 ? false : true)
		}
	}, [indicators])

	useEffect(() => {
		calcultateInitialData()
	}, [])

	useEffect(() => {
		calcultateInitialData()
	}, [dividends]);

	useEffect(() => {
		if (input.isValid) calculate();
	}, [input]);

	const calcultateInitialData = () => {
		if (!dividends || dividends.length < 2) return;

		const fieldsToUpdate 	= []
		const sorted 			= [...dividends].sort((a,b)=> a.year - b.year);
		const first 			= parseFloat(sorted[sorted.length-5]?.dividend);
		const last 				= parseFloat(sorted.at(-1).dividend);
		var cagr 				= 0;

		const years = sorted.length - 1;
		if (first > 0 && years > 0) {
			cagr = (last / first) ** (1 / years) - 1;
		}

		if (cagr > 0) {
			fieldsToUpdate.push({ type: "number", field: "growthRate", payload: (cagr * 100).toFixed(2) });
		}
		else {
			fieldsToUpdate.push({ type: "number", field: "growthRate", payload: inflation });
			fieldsToUpdate.push({ type: "string", field: "growthRateMessage", payload: 'Não foi possível calcular a taxa de crescimento (CAGR) dos dividedos. Utilizando a taxa de inflação do ultimo ano.' });			
		}

		fieldsToUpdate.push({ type: "number", field: "lastDividend", payload: last });
		
		dispatch({type: 'batchUpdate', payload: fieldsToUpdate})
	}
	
	const calculate = () => {
		const g  = input.growthRate / 100;
		const r  = input.discountRate / 100;
		const g2 = input.gLongTerm / 100;
		const N  = input.projectionYears;

		let dividend = input.lastDividend;
		const projections = [];
		let pv = 0;

		// 1. Projeções + VP
		for (let t = 1; t <= N; t++) {
			dividend = dividend * (1 + g);
			const discounted = dividend / Math.pow(1 + r, t);
			pv += discounted;
			projections.push({ year: t, dividend });
		}

		setProjected(projections);

		// 2. Dividendo do primeiro ano após projeções
		const D_Nplus1 = dividend * (1 + g2); 

		// 3. Valor Terminal no final do ano N
		const TV = D_Nplus1 / (r - g2);

		// 4. Valor presente do terminal
		const pvTV = TV / Math.pow(1 + r, N);
		setPvTerminal(pvTV);

		// 5. Preço justo
		setFairPrice(pv + pvTV);
	};

	if (!company || !dividends) return

	return (
		<Card>
			<CardContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
				<Typography variant="h5" sx={{ mb: 2 }}>
					Dividend Discount Model
					{ usageWarning && (
						<Tooltip title="Não houve pagamento consecutivos e crescentes de dividendos nos últimos 5 anos." componentsProps={{tooltip: {sx: {fontSize: 12}}}}>
							<IconButton size="medium"><WarningIcon  fontSize="medium" sx={{ color: 'red' }} /></IconButton>
						</Tooltip>
					)}
				</Typography>

				<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
					<Box sx={{ flex: 1 }}>

						<TextField
							label="Último dividendo anual"
							type="number"
							value={input.lastDividend}
							onChange={(e)=>dispatch({type:"update", field:"lastDividend", payload:e.target.value})}
							error={input.errors?.lastDividend}
							helperText={input.errors?.lastDividend}
							fullWidth sx={{ mb: 1 }}
						/>

						<TextField
							label="Taxa de crescimento (%)"
							type="number"
							value={input.growthRate}
							onChange={(e)=>dispatch({type:"update", field:"growthRate", payload:e.target.value})}
							error={input.errors?.growthRate}
							helperText={input.errors?.growthRate}
							fullWidth sx={{ mb: 1 }}
							InputProps={{
								endAdornment: input.growthRateMessage && (
								<InputAdornment position="end">
									<Tooltip title={input.growthRateMessage} componentsProps={{tooltip: {sx: {fontSize: 12}}}}>
										<IconButton size="medium"><WarningIcon  fontSize="medium" sx={{ color: 'red' }} /></IconButton>
									</Tooltip>
								</InputAdornment>
								),
							}}								
						/>

						<TextField
							label="Taxa de desconto (%)"
							type="number"
							value={input.discountRate}
							onChange={(e)=>dispatch({type:"update", field:"discountRate", payload:e.target.value})}
							error={input.errors?.discountRate}
							helperText={input.errors?.discountRate}
							fullWidth sx={{ mb: 1 }}
						/>

						<TextField
							label="Crescimento perpétuo (%)"
							type="number"
							value={input.gLongTerm}
							onChange={(e)=>dispatch({type:"update", field:"gLongTerm", payload:e.target.value})}
							error={input.errors?.gLongTerm}
							helperText={input.errors?.gLongTerm}
							fullWidth sx={{ mb: 1 }}
						/>

						<TextField
							label="Anos de projeção"
							type="number"
							value={input.projectionYears}
							onChange={(e)=>dispatch({type:"update", field:"projectionYears", payload:e.target.value})}
							error={input.errors?.projectionYears}
							helperText={input.errors?.projectionYears}
							fullWidth sx={{ mb: 1 }}
							inputProps={{ min: 1, max: 20 }}
						/>

					</Box>

					<Divider flexItem orientation="vertical" />

					<Box sx={{ flex: 1 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Ano</TableCell>
									<TableCell align="right">Dividendo projetado</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{projected.map((p,i)=>(
									<TableRow key={i}>
										<TableCell>{p.year}</TableCell>
										<TableCell align="right">{formatCurrency(p.dividend, 'SMALL', company.currency)}</TableCell>
									</TableRow>
								))}

								{pvTerminal !== null && (
									<TableRow>
										<TableCell sx={{ fontWeight: "bold" }}>Valor Terminal (VP)</TableCell>
										<TableCell align="right" sx={{ fontWeight: "bold" }}>
											{formatCurrency(pvTerminal, 'SMALL', company.currency)}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</Box>
				</Stack>

				{fairPrice && (
					<Box 
						mt={"auto"} 
						mb={5} 
						p={2} 
						sx={{
							backgroundColor: stockPrice < fairPrice ? "#e8f5e9" : "#ffebee",
							borderRadius: 2,
							border: "2px solid " + (stockPrice < fairPrice ? "#2e7d32" : "#c62828"),
							cursor: "help"
						}}>
						<Typography variant="h5" align="center" sx={{ fontWeight: "bold", color: stockPrice < fairPrice ? "#2e7d32" : "#c62828" }}>
							💰 Preço justo por ação: {formatCurrency(fairPrice, 'SMALL', company.currency)}
						</Typography>
					</Box>
				)}

			</CardContent>
		</Card>
	);
};

const reducer = (inputFields, action) => {
	let updatedFields = inputFields

	switch (action.type) {

		case 'update' : {
			updatedFields = {
				...updatedFields,
				[action.field] : parseFloat(action.payload)
			};
			break;
		}

		case 'batchUpdate' : {
			action.payload.forEach( (update) => {
				switch (update.type) {
					case 'number' :
						update.payload = parseFloat(update.payload);
						break;
					case 'string' :
						update.payload = update.payload.toString();
						break;
				}

				updatedFields = {
					...updatedFields,
					[update.field] : update.payload
				}
			});
			break;
		}
	}

	const zodParseResults = inputFieldsSchema.safeParse(updatedFields)

	updatedFields.isValid 	= zodParseResults.success
	updatedFields.errors	= parseZodErrors(zodParseResults)

	return updatedFields
};

const inputFieldsSchema = z.object({
	lastDividend: z.number().min(0.0001),
	growthRate: z.number().min(0).max(100),
	discountRate: z.number().min(1).max(100),
	projectionYears: z.number().int().min(1).max(20),
	gLongTerm: z.number().min(0).max(10)
});

const parseZodErrors = (pErrors) => {
	return pErrors.error?.issues?.reduce( (errors, e) => ({...errors, [e.path[0]] : e.message}), {})
}

export default DividendDiscountModel;
